// deps
import React, { ReactElement, memo, useRef, useCallback, useMemo, useState } from "react";

// internal-deps
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as CloseIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/clearLight/24";
import { CVV_BOTTOM_SHEET_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { BottomSheet, BottomSheetChildrenProps } from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { useKeyboardState } from "@swiggy-private/checkout-core";

// components
import Cvv from "@payment-ui/components/payment-method/card/components/Cvv";
import { CardNumberSubname } from "@payment-ui/widgets/payment-method-name/v4/components/card-number-subname";

// styles
import { CloseButton, Container, CvvContainerTitle, CvvInputContainer, CvvInputSubmitCTA } from "./styles";

// interfaces
import { CardPaymentMethodData } from "@payment-ui/interfaces";

// constants
import { CONTINUE, CVV_BOTTOMSHEET_TITLE } from "@payment-ui/constants/Card";

// helpers
import { getValidCardCvvLength } from "@payment-ui/helpers/Card";
import { validateCVV } from "@payment-ui/helpers/Cvv";

interface IProps {
    methodData: CardPaymentMethodData;
    cardSubName?: string;
    onCloseCvvBs: VoidFunction;
    onCardPayAction: (cvv: string) => void;
}

const CvvBottomSheet = (props: IProps) => {
    const cvv = useRef("");
    const [validCvv, setValidCvv] = useState(true);
    const [isButtonEnabled, setButtonEnabled] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    useKeyboardState(containerRef);

    const { cardSubName, onCloseCvvBs, onCardPayAction, methodData } = props;

    const validCvvLength = useMemo(() => getValidCardCvvLength(methodData), [methodData]);

    const onClick = useCallback(
        (e: React.MouseEvent): void => {
            e.preventDefault();

            if (validateCVV(methodData, cvv.current, true)) {
                setValidCvv(true);
                onCardPayAction(cvv.current);
                return;
            }

            setValidCvv(false);
        },
        [methodData, onCardPayAction],
    );

    const onCvvChange = useCallback(
        (value: string): void => {
            cvv.current = value;
            if (!validCvv) {
                setValidCvv(true);
            }
            const isCvvLengthValid = validateCVV(methodData, cvv.current, true);
            setButtonEnabled(isCvvLengthValid);
        },
        [methodData, validCvv],
    );

    const customBottomSheetCloseIcon = (
        <CloseButton>
            <GenericIcon Icon={CloseIcon} onClick={onCloseCvvBs} />
        </CloseButton>
    );

    return (
        <BottomSheet
            closeIcon={customBottomSheetCloseIcon}
            data-testid={CVV_BOTTOM_SHEET_TEST_IDS.CONTAINER}
            onClose={onCloseCvvBs}
        >
            {(_bProps: BottomSheetChildrenProps): ReactElement => (
                <Container ref={containerRef}>
                    {cardSubName && (
                        <CvvContainerTitle>
                            <Text
                                data-testid={CVV_BOTTOM_SHEET_TEST_IDS.TITLE}
                                variant="V2.heading2.neutral.black"
                                color="text_High_Emphasis"
                            >
                                {CVV_BOTTOMSHEET_TITLE}
                            </Text>
                            <CardNumberSubname
                                cardNumber={cardSubName}
                                textVariant="V2.heading2.neutral.black"
                                textColor="text_High_Emphasis"
                                showDivider={false}
                                bulletRadius={4}
                                isTurboPaymentMethod={true}
                            />
                        </CvvContainerTitle>
                    )}

                    <CvvInputContainer>
                        <Cvv
                            valid={validCvv}
                            onChange={onCvvChange}
                            autoFocus
                            validCvvLength={validCvvLength}
                            showFullWidthInputButton
                        />

                        <CvvInputSubmitCTA>
                            <FullWidthButton
                                onClick={onClick}
                                data-testid={CVV_BOTTOM_SHEET_TEST_IDS.PAY_BUTTON}
                                isDisabled={!isButtonEnabled}
                                variant="specialPositive"
                            >
                                {CONTINUE}
                            </FullWidthButton>
                        </CvvInputSubmitCTA>
                    </CvvInputContainer>
                </Container>
            )}
        </BottomSheet>
    );
};

export default memo(CvvBottomSheet);
